import { useMemo } from 'react'
import { Avatar } from 'components/Avatar/Avatar'
import { Button, BUTTON_TYPES } from '../../../components/Button'
import { Typography, TYPOGRAPHY_WEIGHT } from 'components/Typography'
import { DeleteModal } from '../DeleteModal/DeleteModal'
import useRouter from '../../../hooks/useRouter'
import { useChatContext } from 'features/Chat/Providers/ChatContextProvider'
import { useChatPopup } from 'features/Chat/Providers/ChatPopupProvider'
import { combineStrings, getNameInitials, safeString } from 'helper/common'
import { useChatAbility } from '../Providers/ChatAbilityProvider'
import { SUBJECTS, ACTIONS, chatSubject } from '../Abilities'
import { CHAT_TYPE, ROLES_NAMES } from 'constants/common'
import { BTN_TXT } from '../../../constants'
import { routes } from '../../../router'
import {
  IChannelChatDetails,
  IGroupChatDetails,
  IPrivateChatDetails
} from 'redux/store/chatDetails/types'
import { ReactComponent as Search16Icon } from 'assets/svg/Search16.svg'
import { ReactComponent as Back24Icon } from 'assets/svg/ArrowBack16.svg'
import { ReactComponent as More16 } from 'assets/svg/More16.svg'
import './styles.scss'
import { Tooltip } from 'components/Tooltip/Tooltip'

const Header = () => {
  const chatContext = useChatContext()
  const { sidebarDrawer, deleteModal } = useChatPopup()
  const { chat } = chatContext.state
  const { navigate } = useRouter()
  const ability = useChatAbility()

  const openChatDetails = () => {
    if (chat?.type) {
      sidebarDrawer.actions.open({ type: chat.type })
    }
  }

  const chatActions = useMemo(() => {
    const actions = [
      {
        text: BTN_TXT.CHAT_DETAILS,
        onClick: openChatDetails
      }
    ]
    if (
      ability.can(ACTIONS.DELETE, chatSubject(SUBJECTS.PRIVATE_CHAT, chat)) ||
      ability.can(ACTIONS.DELETE, chatSubject(SUBJECTS.GROUP_CHAT, chat))
    ) {
      actions.push({
        text: BTN_TXT.DELETE_CHAT,
        onClick: deleteModal.actions.open
      })
    }
    return actions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.type, chat?.owner_id])

  const handleClickItem = () => {
    navigate(`${routes.chat}`, { replace: true })
  }

  const handleSearchBar = () => {
    chatContext?.actions.handleIsSearch(!chatContext?.state.isSearch)
  }

  const avatarProps = () => {
    switch (chat?.type) {
      case CHAT_TYPE.PRIVATE:
        const privateChatDetails = chat as IPrivateChatDetails | null

        return {
          name: privateChatDetails?.full_name
            ? getNameInitials(privateChatDetails.full_name)
            : 'PC',
          src: privateChatDetails?.avatar?.file_preview
        }
      case CHAT_TYPE.CHANNEL:
        return { name: 'Ch', isChannel: true }
      case CHAT_TYPE.GROUP:
        return { name: 'GC' }
      default:
        return { name: '' }
    }
  }

  const captionText = () => {
    switch (chat?.type) {
      case CHAT_TYPE.PRIVATE:
        const privateChatDetails = chat as IPrivateChatDetails | null

        return privateChatDetails?.role
          ? ROLES_NAMES[privateChatDetails.role]
          : ''
      case CHAT_TYPE.CHANNEL:
        const channelChatDetails = chat as IChannelChatDetails | null

        return channelChatDetails?.rfp_number
      case CHAT_TYPE.GROUP:
        return `${chat?.members_count} member${
          chat?.members_count !== 1 ? 's' : ''
        }`
    }
  }

  const captionComponent = () => {
    const facilityName = (chat as IPrivateChatDetails)?.facility_name
    // add details about user facility in caption if it exists
    if (chat?.type === CHAT_TYPE.PRIVATE && facilityName) {
      const fullInfo = combineStrings(captionText(), facilityName)
      return (
        <Tooltip title={fullInfo}>
          <div>
            <Typography.Caption className="chat-list-feature-item__caption text-ellipsis">
              {fullInfo}
            </Typography.Caption>
          </div>
        </Tooltip>
      )
    }
    return (
      <Typography.Caption className="chat-list-feature-item__caption">
        {captionText()}
      </Typography.Caption>
    )
  }

  const chatName = () => {
    switch (chat?.type) {
      case CHAT_TYPE.PRIVATE:
        const privateChatDetails = chat as IPrivateChatDetails | null

        return privateChatDetails?.full_name
      case CHAT_TYPE.CHANNEL:
        const channelChatDetails = chat as IChannelChatDetails | null

        return channelChatDetails?.rfp_name
      case CHAT_TYPE.GROUP:
        const groupChatDetails = chat as IGroupChatDetails | null

        return groupChatDetails?.name
    }
  }

  return (
    <div className="chat__header chat-header">
      <div className="row space-between full-width align-center">
        <div className="column">
          <div className="row align-center">
            <div className="chat-header-arrow-back-wrapper mr-10">
              <Back24Icon onClick={handleClickItem} />
            </div>
            <div
              className="mr-12 chat-header-avatar"
              onClick={openChatDetails}
              role="presentation"
            >
              <Avatar {...avatarProps()} />
            </div>
            <div className="column space-between">
              <div className="row">
                <div className="column">
                  <Typography.Headline6
                    className="chat-list-feature-item__name"
                    weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
                    onClick={openChatDetails}
                  >
                    {safeString(chatName())}
                  </Typography.Headline6>
                  {captionComponent()}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row gap-10">
          <Button.Icon
            icon={<Search16Icon />}
            type={BUTTON_TYPES.GHOST}
            onClick={handleSearchBar}
            medium
          />
          <Button.Menu
            icon={<More16 />}
            type={BUTTON_TYPES.GHOST}
            options={chatActions}
            iconOnly
            hideArrow
            medium
          />
        </div>
        <DeleteModal />
      </div>
    </div>
  )
}

export default Header
